import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `admin.systems` in Brazilian Portuguese.
 *
 * Declared `: Messages['admin']['systems']`, never inferred — that is the completeness
 * guarantee (PRD-27 LOC-011): a missing key, an extra key, or a function whose signature
 * drifted is a `bun run typecheck` failure rather than a blank on a screen.
 *
 * ── The vocabulary this file is bound to (ADR-0018) ──
 *
 * *sistema conectado*, *agente*, *grupo*, *pessoa/pessoas*, *habilidade*, *etapa* (never
 * *passo*), *execução*. Register is `você`, informal-professional.
 *
 * The terms of art stay in English on purpose (D-15): `endpoint`, `token`, `MCP`,
 * `Model Context Protocol`. A Brazilian administrator reads those in English in every tool
 * they run, and this is the administrator plane — AGM-022 entitles them to the technical
 * name of the thing they are operating.
 *
 * ── Agreement, which is not optional here ──
 *
 * A *sistema* is masculine ("Pausado", "Conectado"); an *etapa* is feminine ("não
 * alcançada"); a capability — the thing this plane keeps calling "it" — is feminine in
 * Portuguese, so the review surface says "Ligada", "Desligada", "não classificada" and
 * "Nenhuma delas muda nada". English carries none of this and so gives no hint that it has
 * to be decided, which is why it is decided once, here, rather than per sentence.
 */
export const systems: Messages['admin']['systems'] = {
  title: 'Sistemas conectados',
  lead: 'Os sistemas externos que esta plataforma pode alcançar, o que cada um pode fazer e com qual conta ele faz isso.',

  connect: 'Conectar um sistema',
  empty: 'Nada conectado ainda',
  emptyBody:
    'Os agentes já conseguem buscar no conhecimento da própria organização sem nada disso. Conecte um sistema quando quiser que eles alcancem outra coisa — um gerenciador de chamados, um sistema de documentos, uma central de atendimento.',

  loading: 'Carregando os sistemas conectados…',
  unavailable: 'Não foi possível carregar a lista de sistemas conectados',
  unavailableBody: 'O problema é da plataforma, não dos sistemas. Nada foi desconectado.',

  never: 'nunca verificado',
  // `when` comes from `formatAgo`, which in Portuguese already carries its preposition
  // ("há 5 min."), so this sentence must not add a second one (LOC-022).
  checked: (when) => `verificado ${when}`,
  toolCount: (fmt, count) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} coisa que ele sabe fazer`
      : `${fmt.formatCount(count)} coisas que ele sabe fazer`,
  capabilityCount: (fmt, live, total) =>
    total === 0
      ? 'nada descoberto ainda'
      : live === 0
        ? pluralCategory(fmt.locale, total) === 'one'
          ? `${fmt.formatCount(total)} disponível, nenhuma ligada`
          : `${fmt.formatCount(total)} disponíveis, nenhuma ligada`
        : pluralCategory(fmt.locale, live) === 'one'
          ? `${fmt.formatCount(live)} de ${fmt.formatCount(total)} ligada`
          : `${fmt.formatCount(live)} de ${fmt.formatCount(total)} ligadas`,
  disabled: 'Pausado',
  disabledHint: 'Nenhum agente consegue alcançar este sistema. As configurações e as decisões dele ficam guardadas.',
  open: 'Abrir',
  openLabel: (name) => `Abrir ${name}`,
  noCredential: 'sem credencial',

  access: {
    title: 'Quem pode usar',
    lead: 'As pessoas cujas conversas podem chamar este sistema, por meio de qualquer agente. A mudança vale a partir da próxima mensagem delas.',
    remoteLead:
      'As pessoas cujas conversas podem passar trabalho para este agente remoto, por meio de qualquer agente. A mudança vale a partir da próxima mensagem delas.',
    label: 'Quem pode usar:',
    everyone: 'Todas as pessoas que entram',
    onlyGroups: 'Somente pessoas em',
    more: (count) => `+${count}`,
    moreLabel: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `e mais ${fmt.formatCount(count)} grupo`
        : `e mais ${fmt.formatCount(count)} grupos`,
    nobody: 'Ninguém pode usar',
    nobodyHint:
      'Os grupos aos quais ele estava limitado não existem mais aqui, então nenhum agente pode usá-lo. Escolha de novo quem pode usar.',
    change: 'Alterar',
    changeLabel: (name) => `Alterar quem pode usar ${name}`,
    save: 'Salvar',
    saving: 'Salvando…',
    cancel: 'Cancelar',
    saved: 'Salvo. Vale a partir da próxima mensagem delas.',
    stale:
      'Alguém alterou isto enquanto você editava, então nada foi salvo. A configuração atual está à mostra — escolha de novo.',
    failed: 'Essa alteração não foi salva. Tente de novo.'
  },

  remote: {
    title: 'Agentes remotos',
    lead: 'Agentes operados por outras organizações ou sistemas, para os quais os agentes desta plataforma podem passar trabalho.',
    empty: 'Nenhum agente remoto registrado',
    emptyBody:
      'Um agente remoto é registrado, revisado e ligado pela API de administração. Depois disso, quem pode usar é escolhido aqui.',
    loading: 'Carregando os agentes remotos…',
    unavailable: 'Não foi possível carregar os agentes remotos',
    unavailableBody: 'O problema é da plataforma, não dos agentes remotos. Nada foi alterado.',
    off: 'Desligado',
    offHint: 'Nenhum agente pode passar trabalho para ele até que um administrador revise o cartão dele e o ligue.',
    trust: {
      untrusted: 'não revisado',
      reviewed: 'revisado',
      first_party: 'próprio'
    },
    skills: (fmt, count) =>
      count === 0
        ? 'nenhuma habilidade listada'
        : pluralCategory(fmt.locale, count) === 'one'
          ? `${fmt.formatCount(count)} habilidade`
          : `${fmt.formatCount(count)} habilidades`,
    unnamed: 'Cartão ainda não lido'
  },

  flow: {
    nameTaken: 'Já existe um sistema conectado com esse nome. Escolha outro nome.',
    title: 'Conectar um sistema',
    lead: 'Cole o endereço do sistema. Todo o resto é deduzido do que ele responder.',
    addressLabel: 'Endereço',
    addressPlaceholder: 'https://example.com/mcp',
    addressHint:
      'O endereço do endpoint do próprio sistema, que a documentação dele costuma chamar de server ou endpoint URL. Não é o site dele.',
    check: 'Verificar',
    checking: 'Perguntando ao sistema…',
    recheck: 'Verificar de novo',
    cancel: 'Cancelar',
    back: 'Voltar',

    stagesTitle: 'O que a plataforma encontrou',
    stagesLead: 'Cada etapa, e o que ela significa. Uma etapa só é tentada depois que a de cima passa.',

    stage: {
      address: 'Endereço',
      reach: 'Alcançável',
      protocol: 'Fala o protocolo',
      identity: 'Diz quem é',
      authorization: 'Credenciais',
      offer: 'O que ele oferece'
    },
    stagePending: 'não alcançada',
    stageChecking: 'verificando',

    evidence: {
      answered: (fmt, ms) => `respondeu em ${fmt.formatCount(ms)} ms`,
      protocolLegacy: (version) => `${version} (uma revisão anterior, que esta plataforma também fala)`,
      callsItself: (name) => `se apresenta como ${name}`,
      callsItselfVersioned: (name, version) => `se apresenta como ${name} ${version}`,
      credentialAccepted: 'a credencial foi aceita',
      credentialNone: 'nenhuma necessária',
      signsPeopleIn:
        'Este sistema autentica as pessoas por conta própria. Ele emite as próprias credenciais em vez de aceitar uma que você cole.'
    },

    claimTitle: 'O que o sistema diz sobre si mesmo',
    claimHint: 'São as palavras dele, não as da plataforma. Nada aqui foi verificado.',
    noIdentity: 'Este sistema não disse como se chama.',

    nameLabel: 'Nome curto',
    nameHint:
      'Usado nos registros de auditoria e no endereço desta página. Letras minúsculas, números e hifens. Não dá para mudar depois.',
    descriptionLabel: 'O que ele é, nas suas palavras',
    descriptionHint: 'Lido por outros administradores, nunca por um agente.',
    descriptionPlaceholder: 'O gerenciador de chamados em que a central de atendimento trabalha.',

    trustLabel: 'Quanto este sistema é confiável',
    trustHint:
      'Isso decide o peso que a plataforma dá ao que o sistema diz sobre as próprias ferramentas. Nunca decide o que uma ferramenta pode fazer — isso continua com você.',
    trust: {
      untrusted: {
        label: 'Não revisado',
        note: 'Nada do que ele diz sobre as próprias ferramentas conta. Toda ferramenta começa exigindo aprovação, e você classifica cada uma na mão.'
      },
      reviewed: {
        label: 'Revisado',
        note: 'Alguém já verificou quem opera este sistema. Uma ferramenta que ele descreve como somente leitura pode ser proposta como tal, para você aceitar.'
      },
      first_party: {
        label: 'Nosso',
        note: 'Esta organização opera este sistema. As descrições dele são tratadas como exatas, e você ainda confirma cada uma.'
      }
    },

    credentialTitle: 'Este sistema quer uma credencial',
    credentialNone: 'Este sistema não pediu credencial.',
    credentialTokenLabel: 'Onde o token fica guardado',
    credentialTokenPlaceholder: 'env:JIRA_TOKEN',
    credentialTokenHint:
      'O nome do lugar onde o token está guardado, nunca o token. A plataforma lê esse lugar na hora da chamada e não guarda cópia — então rotacionar o segredo basta, e nada aqui precisa mudar.',
    credentialOauthTitle: 'Este sistema autentica as pessoas',
    credentialOauthBody:
      'Ele emite as próprias credenciais em vez de aceitar uma que você cole. Conectar abre a página de entrada dele nesta janela.',
    modeLabel: 'Com qual conta ele deve agir?',
    modeHint:
      'Isso não pode ser misturado. Toda chamada a este sistema roda como uma coisa ou como a outra, e o registro do que aconteceu diz qual foi.',
    mode: {
      delegated: {
        label: 'A conta de cada pessoa',
        note: 'Cada pessoa entra por conta própria na primeira vez que um agente precisar. Os registros do próprio sistema mostram quem realmente fez o quê, e quem sai leva o acesso junto.'
      },
      service_account: {
        label: 'Uma conta para todo mundo',
        note: 'Você entra uma vez e todos os agentes usam essa conta. Mais simples, e o sistema do outro lado não consegue distinguir seus colegas — tudo que ele registrar vai dizer que foi você.'
      }
    },
    signIn: 'Conectar e entrar',
    signingIn: 'Abrindo a página de entrada…',
    returnedOk: (name) => `${name} está conectado.`,
    returnedFailedTitle: 'Essa entrada não foi concluída',
    scopeLabel: 'Ele vai pedir',

    submit: 'Conectar',
    submitting: 'Conectando…',
    connected: 'Conectado',
    connectedBody: (fmt, count) =>
      count === 0
        ? 'Nada está ligado ainda. Revise o que ele oferece para decidir o que os agentes podem usar.'
        : pluralCategory(fmt.locale, count) === 'one'
          ? `${fmt.formatCount(count)} coisa para revisar. Nada é ligado até você mandar.`
          : `${fmt.formatCount(count)} coisas para revisar. Nada é ligado até você mandar.`,
    review: 'Revisar o que ele oferece',

    stdioTitle: 'Isso parece um comando, não um endereço',
    stdioBody:
      'Alguns sistemas são descritos como um comando para rodar na sua própria máquina. Esta plataforma não vai rodar um: seria executar o código de outra pessoa ao lado do banco de dados e da chave do modelo. Peça a quem é responsável pelo sistema um endereço hospedado.'
  },

  review: {
    title: 'O que este sistema oferece',
    lead: 'Nada aqui pode ser usado por um agente até você ligar e dizer o que ele pode fazer.',
    empty: 'Nada descoberto ainda',
    emptyBody: 'Pergunte ao sistema o que ele oferece — a plataforma não olha desde que ele foi conectado.',
    discover: 'Perguntar de novo o que ele oferece',
    discovering: 'Perguntando…',

    claimedBy: (system) => `O que ${system} diz`,
    claimHint: 'A descrição do próprio sistema. Ninguém verificou.',
    behaviourTitle: 'E o que ele diz sobre como isso se comporta',
    behaviourNone: 'Ele não diz nada sobre como isso se comporta.',

    hint: {
      readOnly: 'só lê, não muda nada',
      writes: 'muda alguma coisa',
      destructive: 'destrói alguma coisa',
      notDestructive: 'nada do que ela muda é destruído',
      idempotent: 'rodar duas vezes é o mesmo que rodar uma',
      openWorld: 'alcança fora do sistema',
      closedWorld: 'fica dentro do sistema'
    },

    suggestionTitle: 'A plataforma sugere',
    suggestionAccept: 'Usar esta',
    classLabel: 'O que ela pode fazer',

    enable: 'Ligar',
    disable: 'Desligar',
    enabled: 'Ligada',
    notEnabled: 'Desligada',
    saving: 'Salvando…',
    selectLabel: (name) => `Selecionar ${name}`,

    quarantinedTitle: 'Retida — isto mudou',
    reReviewTitle: 'Retida — nunca revisada com isto à vista',
    clearQuarantine: 'Eu li a mudança',
    refusedTitle: 'A plataforma não vai levar esta',

    selected: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} selecionada`
        : `${fmt.formatCount(count)} selecionadas`,
    selectAllReadable: 'Selecionar tudo que ele diz que só lê',
    clearSelection: 'Limpar',
    applyTitle: 'Ligar o que está selecionado',
    applyBody: (fmt, { total, writing, unattended }) => {
      const lead =
        pluralCategory(fmt.locale, total) === 'one'
          ? `${fmt.formatCount(total)} vai ficar disponível para os agentes que receberem acesso.`
          : `${fmt.formatCount(total)} vão ficar disponíveis para os agentes que receberem acesso.`
      if (writing === 0) return `${lead} Nenhuma delas muda nada.`
      const changes =
        pluralCategory(fmt.locale, writing) === 'one'
          ? `${fmt.formatCount(writing)} delas muda alguma coisa`
          : `${fmt.formatCount(writing)} delas mudam alguma coisa`
      if (unattended === 0) return `${lead} ${changes}, e uma pessoa é consultada antes de cada uma dessas rodar.`
      const unattendedPhrase =
        pluralCategory(fmt.locale, unattended) === 'one'
          ? `${fmt.formatCount(unattended)} dessas roda sem ninguém ser consultado`
          : `${fmt.formatCount(unattended)} dessas rodam sem ninguém ser consultado`
      return `${lead} ${changes}, e ${unattendedPhrase}.`
    },
    apply: 'Ligar todas',
    applying: 'Ligando…',
    applyFailed: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} não pôde ser ligada:`
        : `${fmt.formatCount(count)} não puderam ser ligadas:`
  },

  detail: {
    diagnose: 'Verificar este sistema',
    diagnosing: 'Verificando…',
    pause: 'Pausar',
    resume: 'Retomar',
    pauseConfirm: 'Pausar este sistema?',
    pauseBody:
      'Todo agente que recebeu acesso a alguma coisa que ele faz para de conseguir alcançá-lo, na hora. O trabalho já em andamento termina ou falha por conta própria. Nada é excluído e nada é esquecido — retomar traz tudo de volta.',
    remove: 'Remover',
    removeConfirm: 'Remover este sistema?',
    removeBody:
      'Ele deixa de ser alcançável e some desta lista. O que ele oferecia, e cada decisão tomada sobre ele, fica guardado — então um sistema removido por engano volta com o histórico de revisão intacto.',
    identityTitle: 'Identidade',
    connectionTitle: 'Conexão',
    protocolLabel: 'Protocolo',
    protocolHint: 'A revisão do Model Context Protocol que este sistema e a plataforma acertaram entre si.',
    eraModern: 'atual',
    eraLegacy: 'revisão anterior',
    urlLabel: 'Endereço',
    modeLabel: 'Roda como',
    lastCheckedLabel: 'Última verificação',
    offersLabel: 'Ele também oferece',
    offersHint: 'Tipos de coisa que este sistema fornece e que a plataforma ainda não usa.',
    instructionsTitle: 'Orientação que ele oferece',
    instructionsHint: 'Registrada para você. Nunca é entregue a nenhum agente.'
  },

  probeStage: {
    url_invalid: 'O endereço',
    ssrf_blocked: 'O endereço',
    connect: 'Alcançar o sistema',
    version_mismatch: 'O protocolo',
    token_unresolved: 'Credenciais',
    authorization_required: 'Credenciais',
    discover: 'O protocolo',
    tools_list: 'O que ele oferece'
  },
  stageUnhealthy: 'Não está funcionando',

  riskLabel: {
    read: 'Só lê',
    reversible_write: 'Muda algo que dá para desfazer',
    external_communication: 'Manda algo para fora',
    privileged: 'Age com acesso elevado',
    financial: 'Movimenta dinheiro',
    destructive: 'Destrói algo'
  },

  riskNote: {
    read: 'Roda sozinha. Não consegue mudar nada.',
    reversible_write: 'Roda sozinha. O que ela muda dá para voltar atrás.',
    external_communication: 'Uma pessoa é consultada antes. Algo sai da plataforma quando ela roda.',
    privileged: 'Uma pessoa é consultada antes. Ela age com um acesso que a maioria das pessoas não tem.',
    financial: 'Uma pessoa é consultada antes. Dinheiro se movimenta quando ela roda.',
    destructive: 'Uma pessoa é consultada antes. O que ela faz não dá para desfazer.'
  },

  riskUnclassified: 'não classificada'
}
